/**
 * The account's model policy (task 1147 -> service task 1146): its own provider
 * keys, its allowed providers and Models, and its defaults.
 *
 * Two readers share these queries: the Settings › Account › Models page, and
 * the three model pickers (agent form, inline agent editor, node override),
 * which grey out Model Configs on Models the account does not allow. Both read
 * the same cache entries, so a change on the page reaches an open picker on the
 * next read.
 *
 * Writes use `mutateAsync` at the call site so the service's refusal (a failed
 * key verification, a 409 naming the agents that still use a Model) reaches the
 * dialog that asked. Every write invalidates the whole `['account', 'models']`
 * prefix: a key changes reachability, a provider toggle changes every Model's
 * allowed state, and a move changes the agents-using counts.
 */
import { useMemo } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { accountModelsApi } from '@/services/api';
import type { SubscriptionSummary } from '@/types/billing';
import type {
  AccountAllowedModel,
  AccountAllowedModelGroup,
  AccountModelProvider,
  UpdateAccountModelInput,
} from '@/types/accountModels';

export const ACCOUNT_MODELS_QUERY_KEY = ['account', 'models'] as const;
export const ACCOUNT_MODEL_PROVIDERS_QUERY_KEY = ['account', 'models', 'providers'] as const;
export const ACCOUNT_ALLOWED_MODELS_QUERY_KEY = ['account', 'models', 'allowed'] as const;

/** Where the pickers and the billing page point the User to change the policy. */
export const ACCOUNT_MODELS_SETTINGS_PATH = '/settings/account/models';

interface ReadOptions {
  enabled?: boolean;
}

export const useAccountModelProviders = ({ enabled = true }: ReadOptions = {}) =>
  useQuery<AccountModelProvider[]>({
    queryKey: ACCOUNT_MODEL_PROVIDERS_QUERY_KEY,
    queryFn: () => accountModelsApi.listProviders().then((res) => res.data.data),
    enabled,
    retry: false,
    staleTime: 30_000,
  });

export const useAccountAllowedModels = ({ enabled = true }: ReadOptions = {}) =>
  useQuery<AccountAllowedModelGroup[]>({
    queryKey: ACCOUNT_ALLOWED_MODELS_QUERY_KEY,
    queryFn: () => accountModelsApi.listAllowed().then((res) => res.data.data),
    enabled,
    retry: false,
    staleTime: 30_000,
  });

function useInvalidateAccountModels() {
  const queryClient = useQueryClient();
  return () => queryClient.invalidateQueries({ queryKey: ACCOUNT_MODELS_QUERY_KEY });
}

/** Add (`replace: false`) or replace the account's key for a provider. Verify-then-store on the service. */
export const useSaveAccountProviderKey = () => {
  const invalidate = useInvalidateAccountModels();
  return useMutation({
    mutationFn: ({ providerId, key, replace }: { providerId: string; key: string; replace: boolean }) =>
      (replace ? accountModelsApi.replaceKey(providerId, key) : accountModelsApi.addKey(providerId, key)).then(
        (res) => res.data.data,
      ),
    onSuccess: invalidate,
  });
};

export const useVerifyAccountProviderKey = () => {
  const invalidate = useInvalidateAccountModels();
  return useMutation({
    mutationFn: (providerId: string) => accountModelsApi.verifyKey(providerId).then((res) => res.data.data),
    onSuccess: invalidate,
  });
};

export const useRemoveAccountProviderKey = () => {
  const invalidate = useInvalidateAccountModels();
  return useMutation({
    mutationFn: (providerId: string) => accountModelsApi.removeKey(providerId).then((res) => res.data.data),
    onSuccess: invalidate,
  });
};

export const useSetAccountProviderEnabled = () => {
  const invalidate = useInvalidateAccountModels();
  return useMutation({
    mutationFn: ({ providerId, enabled }: { providerId: string; enabled: boolean }) =>
      accountModelsApi.setProviderEnabled(providerId, enabled).then((res) => res.data.data),
    onSuccess: invalidate,
  });
};

export const useSetAccountDefaultProvider = () => {
  const invalidate = useInvalidateAccountModels();
  return useMutation({
    mutationFn: (providerId: string | null) => accountModelsApi.setDefaultProvider(providerId).then((res) => res.data.data),
    onSuccess: invalidate,
  });
};

export const useUpdateAccountModel = () => {
  const invalidate = useInvalidateAccountModels();
  return useMutation({
    mutationFn: ({ modelId, data }: { modelId: string; data: UpdateAccountModelInput }) =>
      accountModelsApi.updateModel(modelId, data).then((res) => res.data.data),
    onSuccess: invalidate,
  });
};

/** Repoint the agents on one Model to another; their Model Configs keep their settings. */
export const useMoveAccountModelAgents = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ modelId, targetModelId }: { modelId: string; targetModelId: string }) =>
      accountModelsApi.moveAgents(modelId, targetModelId).then((res) => res.data.data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ACCOUNT_MODELS_QUERY_KEY });
      queryClient.invalidateQueries({ queryKey: ['agents'] });
      queryClient.invalidateQueries({ queryKey: ['agent'] });
    },
  });
};

/**
 * Whether a Model may be chosen in this account: its provider is on, the
 * account has not excluded it, and the account's own key (if any) can reach it.
 */
export function isAccountModelUsable(group: AccountAllowedModelGroup, model: AccountAllowedModel): boolean {
  // The service computes this as `effectiveAllowed` (task 1146); the fallback
  // derives the same answer for a build that predates the field.
  if (typeof model.effectiveAllowed === 'boolean') return model.effectiveAllowed;
  return group.provider.enabled !== false && model.allowed && model.reachable !== false;
}

/** What a model picker needs from the policy: which Models to grey out and which to mark. */
export interface AccountModelPicks {
  /** Model UUIDs the account does not allow (excluded, provider off, or not on the account key). */
  disallowed: Set<string>;
  /** Model UUIDs that are the account default for their provider. */
  defaults: Set<string>;
}

/**
 * The policy as the pickers apply it, or `null` while it is unknown (loading,
 * refused, or a service build without the routes). Null leaves the picker
 * exactly as it was before the policy existed; the service still enforces the
 * policy on save and at run time, so an unknown policy never lets a disallowed
 * Model through.
 */
export function useAccountModelPicks(): AccountModelPicks | null {
  const allowed = useAccountAllowedModels();
  const providers = useAccountModelProviders();

  return useMemo(() => {
    if (!allowed.isSuccess || !allowed.data) return null;
    const providerOff = new Set((providers.data ?? []).filter((p) => !p.enabled).map((p) => p.id));
    const disallowed = new Set<string>();
    const defaults = new Set<string>();
    for (const group of allowed.data) {
      const off = providerOff.has(group.provider.id);
      for (const model of group.models) {
        if (off || !isAccountModelUsable(group, model)) disallowed.add(model.id);
        else if (model.isDefault) defaults.add(model.id);
      }
    }
    return { disallowed, defaults };
  }, [allowed.isSuccess, allowed.data, providers.data]);
}

/** This period's spend split by who paid the provider, in dollars. */
export interface KeyScopeSpend {
  /** Spend on the account's own keys: paid to the provider, informational. */
  accountKeyDollars: number;
  /** Spend on the platform's keys: billed on the Innovate invoice. */
  platformKeyDollars: number;
  /** The "YYYY-MM" month the figures cover. */
  period: string;
}

const microUsdToDollars = (value: string | number) => {
  const n = typeof value === 'number' ? value : Number(value);
  return Number.isFinite(n) ? n / 1_000_000 : 0;
};

/**
 * Read the key-scope split off the billing subscription summary's `usage`
 * (task 1148: micro-USD strings). `null` when the summary could not be read
 * (it is owner-only, or unavailable) or predates the field: the caller then
 * says the split is unavailable rather than showing a guessed $0.00.
 */
export function keyScopeSpend(summary: SubscriptionSummary | null | undefined): KeyScopeSpend | null {
  const usage = summary?.usage;
  if (!usage) return null;
  return {
    accountKeyDollars: microUsdToDollars(usage.accountKeyCost),
    platformKeyDollars: microUsdToDollars(usage.platformKeyCost),
    period: usage.period,
  };
}
